import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ClinicProvider } from './context/ClinicContext';

// Layouts
import MainLayout from './components/Layout/MainLayout';

// Pages
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Leads from './pages/Leads/Leads';
import LeadDetail from './pages/Leads/LeadDetail';
import Patients from './pages/Patients/Patients';
import PatientDetail from './pages/Patients/PatientDetail';
import Calendar from './pages/Calendar/Calendar';
import Assessments from './pages/Assessments/Assessments';
import Payments from './pages/Payments/Payments';
import Invoices from './pages/Payments/Invoices';
import Staff from './pages/Staff/Staff';
import PatientProtocols from './pages/Staff/PatientProtocols';
import Profile from './pages/Profile/Profile';
import Progress from './pages/Progress/Progress';
import Events from './pages/Events/Events';
import Finance from './pages/Finance/Finance';
import PatientAttendance from './pages/PatientAttendance/PatientAttendance';
import LeaveTypesConfig from './pages/LeaveTypes/LeaveTypesConfig';
import Expenses from './pages/Expenses/Expenses';
import DiagnosisCodes from './pages/Diagnosis/DiagnosisCodes';
import LeadSources from './pages/Configuration/LeadSources';
import LeadStatuses from './pages/Configuration/LeadStatuses';
import TherapyTypes from './pages/Configuration/TherapyTypes';
import InvoiceItems from './pages/Configuration/InvoiceItems';
import EmailSettings from './pages/Configuration/EmailSettings';
import WhatsAppSettings from './pages/Configuration/WhatsAppSettings';
import PatientAttendanceSettings from './pages/Configuration/PatientAttendanceSettings';
import StaffLeaveOpeningSetup from './pages/Configuration/StaffLeaveOpeningSetup';
import JobPositionTypes from './pages/Configuration/JobPositionTypes';
import DocxTemplates from './pages/Configuration/DocxTemplates';
import Clinics from './pages/Configuration/Clinics';
import ClinicMembers from './pages/Configuration/ClinicMembers';
import PlanTypes from './pages/Configuration/PlanTypes';
import Roles from './pages/Configuration/Roles';
import Discussions from './pages/Discussions/Discussions';
import MonthlyReviews from './pages/MonthlyReviews/MonthlyReviews';
import PublicLeadForm from './pages/Public/PublicLeadForm';
import { usePermissions } from './hooks/usePermissions';
import { useClinic } from './context/ClinicContext';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, module, action = 'read' }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const { hasPermission, isTherapist } = usePermissions();
  const { monthlyReviewsTherapistEnabled, monthlyReviewsVisibilityLoaded } = useClinic();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (module && !hasPermission(module, action)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (module === 'monthly_reviews' && isTherapist) {
    if (!monthlyReviewsVisibilityLoaded) {
      return (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      );
    }
    if (!monthlyReviewsTherapistEnabled) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

/** Legacy links used `/invoices/:id` — open invoices list with detail modal. */
const InvoiceDeepLink = () => {
  const { invoiceId } = useParams();
  const q = invoiceId ? `?focusId=${encodeURIComponent(invoiceId)}&openDetail=1` : '';
  return <Navigate to={`/invoices${q}`} replace />;
};

// App Routes
const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} 
      />
      <Route path="/public/lead/:token" element={<PublicLeadForm />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={
          <ProtectedRoute module="dashboard" action="read">
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="leads" element={
          <ProtectedRoute module="leads" action="read">
            <Leads />
          </ProtectedRoute>
        } />
        <Route path="leads/:id" element={
          <ProtectedRoute module="leads" action="read">
            <LeadDetail />
          </ProtectedRoute>
        } />
        <Route path="patients" element={
          <ProtectedRoute module="patients" action="read">
            <Patients />
          </ProtectedRoute>
        } />
        <Route path="patients/:id" element={
          <ProtectedRoute module="patients" action="read">
            <PatientDetail />
          </ProtectedRoute>
        } />
        <Route path="calendar" element={
          <ProtectedRoute module="calendar" action="read">
            <Calendar />
          </ProtectedRoute>
        } />
        <Route path="assessments" element={
          <ProtectedRoute module="assessments" action="read">
            <Assessments />
          </ProtectedRoute>
        } />
        <Route path="payments" element={
          <ProtectedRoute module="payments" action="read">
            <Payments />
          </ProtectedRoute>
        } />
        <Route path="invoices" element={
          <ProtectedRoute module="invoices" action="read">
            <Invoices />
          </ProtectedRoute>
        } />
        <Route path="invoices/:invoiceId" element={
          <ProtectedRoute module="invoices" action="read">
            <InvoiceDeepLink />
          </ProtectedRoute>
        } />
        <Route path="staff" element={
          <ProtectedRoute module="staff" action="read">
            <Staff />
          </ProtectedRoute>
        } />
        <Route path="patient-protocols" element={
          <ProtectedRoute module="protocols" action="read">
            <PatientProtocols />
          </ProtectedRoute>
        } />
        <Route path="discussions" element={
          <ProtectedRoute module="discussions" action="read">
            <Discussions />
          </ProtectedRoute>
        } />
        <Route path="profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="progress" element={
          <ProtectedRoute module="progress" action="read">
            <Progress />
          </ProtectedRoute>
        } />
        <Route path="monthly-reviews" element={
          <ProtectedRoute module="monthly_reviews" action="read">
            <MonthlyReviews />
          </ProtectedRoute>
        } />
        <Route path="events" element={
          <ProtectedRoute module="events" action="read">
            <Events />
          </ProtectedRoute>
        } />
        <Route path="patient-attendance" element={
          <ProtectedRoute module="attendance" action="read">
            <PatientAttendance />
          </ProtectedRoute>
        } />
        <Route 
          path="leave-types" 
          element={
            <ProtectedRoute module="configuration" action="read">
              <LeaveTypesConfig />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="finance" 
          element={
            <ProtectedRoute module="finance" action="read">
              <Finance />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="expenses" 
          element={
            <ProtectedRoute module="expenses" action="read">
              <Expenses />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="diagnosis-codes" 
          element={
            <ProtectedRoute module="configuration" action="read">
              <DiagnosisCodes />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="lead-sources" 
          element={
            <ProtectedRoute module="configuration" action="read">
              <LeadSources />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="lead-statuses" 
          element={
            <ProtectedRoute module="configuration" action="read">
              <LeadStatuses />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="therapy-types" 
          element={
            <ProtectedRoute module="configuration" action="read">
              <TherapyTypes />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="invoice-items" 
          element={
            <ProtectedRoute module="configuration" action="read">
              <InvoiceItems />
            </ProtectedRoute>
          } 
        />
        <Route
          path="email-settings"
          element={
            <ProtectedRoute module="configuration" action="read">
              <EmailSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="whatsapp-settings"
          element={
            <ProtectedRoute module="configuration" action="read">
              <WhatsAppSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="attendance-settings"
          element={
            <ProtectedRoute module="configuration" action="read">
              <PatientAttendanceSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff-leave-opening"
          element={
            <ProtectedRoute module="configuration" action="read">
              <StaffLeaveOpeningSetup />
            </ProtectedRoute>
          }
        />
        <Route
          path="job-position-types"
          element={
            <ProtectedRoute module="configuration" action="read">
              <JobPositionTypes />
            </ProtectedRoute>
          }
        />
        <Route
          path="templates"
          element={
            <ProtectedRoute module="configuration" action="read">
              <DocxTemplates />
            </ProtectedRoute>
          }
        />
        <Route
          path="plan-types"
          element={
            <ProtectedRoute module="configuration" action="read">
              <PlanTypes />
            </ProtectedRoute>
          }
        />
        <Route
          path="roles"
          element={
            <ProtectedRoute allowedRoles={['admin', 'clinic_admin']}>
              <Roles />
            </ProtectedRoute>
          }
        />
        <Route
          path="clinics"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Clinics />
            </ProtectedRoute>
          }
        />
        <Route
          path="clinics/:id/members"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ClinicMembers />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <ClinicProvider>
          <AppRoutes />
          <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1f2937',
              color: '#fff',
              borderRadius: '0.75rem',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        </ClinicProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;

